import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Wallet } from "lucide-react";
import { api } from "@/lib/api";
import { PaymentDateEditor } from "@/components/PaymentDateEditor";
import { PAYMENT_METHOD_LABELS } from "@/components/RecordPaymentDialog";

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const methodLabel = (p) => (p.session_id ? "Credit card" : PAYMENT_METHOD_LABELS[p.method] || "Other");

export const useCustomerPayments = (customerId) => {
  const [data, setData] = useState(null);
  const load = useCallback(() => {
    api.get(`/customers/${customerId}/payments`).then(({ data }) => setData(data)).catch(() => setData({ payments: [], total_paid: 0, count: 0 }));
  }, [customerId]);
  useEffect(() => { load(); }, [load]);
  return [data, load];
};

const PaymentRow = ({ p, onSaved }) => {
  const nav = useNavigate();
  return (
    <tr className={`border-b border-zinc-100 last:border-0 ${p.voided ? "opacity-50" : ""}`} data-testid={`customer-payment-row-${p.id}`}>
      <td className="py-3">
        <PaymentDateEditor invoiceId={p.invoice_id} payment={p} disabled={!p.can_edit_date} onSaved={onSaved} />
      </td>
      <td className="py-3">
        {p.invoice_id ? (
          <button onClick={() => nav(`/app/invoices/${p.invoice_id}`)} className="text-left hover:text-[#FF5A00]" data-testid={`customer-payment-invoice-${p.id}`}>
            <div className="font-medium truncate max-w-[220px]">{p.invoice_title || p.invoice_number || "Invoice"}</div>
            <div className="text-xs text-zinc-500 font-mono-accent">{p.invoice_number}</div>
          </button>
        ) : "—"}
      </td>
      <td className="py-3 text-xs text-zinc-600 hidden sm:table-cell">{methodLabel(p)}{p.voided && <span className="ml-1 text-rose-600 uppercase">void</span>}</td>
      <td className={`py-3 text-right font-mono-accent font-semibold ${p.voided ? "line-through text-zinc-500" : "text-emerald-700"}`}>
        {money(p.amount)}
        {p.amount_refunded > 0 && <div className="text-[10px] text-rose-600 font-normal">-{money(p.amount_refunded)} refunded</div>}
      </td>
    </tr>
  );
};

export const CustomerPaymentsSection = ({ data, onSaved }) => (
  <div id="customer-payments" className="bg-white border border-zinc-200 rounded-xl p-6 card-crisp scroll-mt-6" data-testid="customer-payments-section">
    <div className="flex flex-wrap items-center gap-2 mb-4">
      <Wallet className="w-5 h-5 text-emerald-600" />
      <h2 className="font-heading text-xl font-bold">Payment history</h2>
      <span className="ml-auto text-xs text-zinc-500 font-mono-accent" data-testid="customer-payments-total">
        {data ? `${data.count} · ${money(data.total_paid)} paid` : "…"}
      </span>
    </div>
    {!data ? (
      <div className="text-sm text-zinc-400 py-4">Loading…</div>
    ) : data.payments.length === 0 ? (
      <div className="text-sm text-zinc-500 py-4" data-testid="customer-payments-empty">No payments yet.</div>
    ) : (
      <div className="overflow-x-auto -mx-2 px-2"><table className="w-full text-sm min-w-[340px]">
        <thead className="text-xs uppercase text-zinc-500 border-b border-zinc-100">
          <tr><th className="text-left py-2">Date received</th><th className="text-left py-2">Invoice</th><th className="text-left py-2 hidden sm:table-cell">Method</th><th className="text-right py-2">Amount</th></tr>
        </thead>
        <tbody>{data.payments.map((p) => <PaymentRow key={p.id} p={p} onSaved={onSaved} />)}</tbody>
      </table></div>
    )}
  </div>
);
